// SPDX-License-Identifier: AGPL-3.0-or-later

import {getReactionKey, type ReactionEmoji} from '@app/features/messaging/utils/ReactionUtils';
import {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import type {UserPartial} from '@app/vendor/schema/domains/user/UserResponseSchemas';
import {makeAutoObservable} from 'mobx';

type ReactionUsers = Record<string, User>;
type FetchStatus = 'idle' | 'pending' | 'success' | 'error';
interface Reaction {
	users: ReactionUsers;
	fetchStatus: FetchStatus;
	hasMore: boolean;
	lastUserId: string | null;
	initialFetchLimit: number;
}
type ReactionMap = Record<string, Reaction>;
const createEmptyReaction = (): Reaction => ({
	users: {},
	fetchStatus: 'idle',
	hasMore: true,
	lastUserId: null,
	initialFetchLimit: 0,
});
function normalizeTotalCount(totalCount: number | undefined): number | undefined {
	if (totalCount === undefined || !Number.isFinite(totalCount)) {
		return undefined;
	}
	return Math.max(0, Math.floor(totalCount));
}
function inferHasMore({
	entry,
	pageUserCount,
	requestedLimit,
	responseHasMore,
	totalCount,
}: {
	entry: Reaction;
	pageUserCount: number;
	requestedLimit?: number;
	responseHasMore?: boolean;
	totalCount?: number;
}): boolean {
	if (responseHasMore !== undefined) {
		return responseHasMore;
	}
	if (pageUserCount === 0) {
		return false;
	}
	const normalizedTotalCount = normalizeTotalCount(totalCount);
	if (normalizedTotalCount !== undefined) {
		return Object.keys(entry.users).length < normalizedTotalCount;
	}
	return requestedLimit !== undefined ? pageUserCount >= requestedLimit : false;
}
class MessageReactions {
	reactions: ReactionMap = {};
	private _keysByMessage: Map<string, Set<string>> = new Map();
	constructor() {
		makeAutoObservable(this, {_keysByMessage: false} as never, {autoBind: true});
	}
	getReactionEntry(messageId: string, emoji: ReactionEmoji): Reaction | undefined {
		const reactionKey = getReactionKey(messageId, emoji);
		return this.reactions[reactionKey];
	}
	getReactions(messageId: string, emoji: ReactionEmoji): ReadonlyArray<User> {
		const entry = this.getReactionEntry(messageId, emoji);
		return entry ? Object.values(entry.users) : [];
	}
	getFetchStatus(messageId: string, emoji: ReactionEmoji): FetchStatus {
		const entry = this.getReactionEntry(messageId, emoji);
		return entry?.fetchStatus ?? 'idle';
	}
	getHasMore(messageId: string, emoji: ReactionEmoji): boolean {
		const entry = this.getReactionEntry(messageId, emoji);
		return entry?.hasMore ?? true;
	}
	getLastUserId(messageId: string, emoji: ReactionEmoji): string | null {
		const entry = this.getReactionEntry(messageId, emoji);
		return entry?.lastUserId ?? null;
	}
	getInitialFetchLimit(messageId: string, emoji: ReactionEmoji): number {
		const entry = this.getReactionEntry(messageId, emoji);
		return entry?.initialFetchLimit ?? 0;
	}
	private getOrCreateReactionEntry(messageId: string, emoji: ReactionEmoji): Reaction {
		const key = getReactionKey(messageId, emoji);
		let entry = this.reactions[key];
		if (!entry) {
			entry = createEmptyReaction();
			this.reactions[key] = entry;
			let keys = this._keysByMessage.get(messageId);
			if (!keys) {
				keys = new Set();
				this._keysByMessage.set(messageId, keys);
			}
			keys.add(key);
		}
		return entry;
	}
	handleConnectionOpen(): void {
		this.reactions = {};
		this._keysByMessage.clear();
	}
	handleReactionAdd(messageId: string, userId: string, emoji: ReactionEmoji): void {
		const entry = this.getOrCreateReactionEntry(messageId, emoji);
		const user = Users.getUser(userId);
		if (!user) return;
		entry.users[userId] = user;
	}
	handleReactionRemove(messageId: string, userId: string, emoji: ReactionEmoji): void {
		const entry = this.getReactionEntry(messageId, emoji);
		if (!entry) return;
		delete entry.users[userId];
	}
	handleReactionRemoveAll(messageId: string): void {
		const keys = this._keysByMessage.get(messageId);
		if (!keys) return;
		for (const key of keys) {
			delete this.reactions[key];
		}
		this._keysByMessage.delete(messageId);
	}
	handleReactionRemoveEmoji(messageId: string, emoji: ReactionEmoji): void {
		const entry = this.getOrCreateReactionEntry(messageId, emoji);
		entry.users = {};
		entry.fetchStatus = 'idle';
		entry.hasMore = true;
		entry.lastUserId = null;
		entry.initialFetchLimit = 0;
	}
	handleFetchPending(messageId: string, emoji: ReactionEmoji): void {
		const entry = this.getOrCreateReactionEntry(messageId, emoji);
		entry.fetchStatus = 'pending';
	}
	handleFetchSuccess(
		messageId: string,
		users: ReadonlyArray<UserPartial>,
		emoji: ReactionEmoji,
		requestedLimit?: number,
		responseHasMore?: boolean,
		totalCount?: number,
	): void {
		const entry = this.getOrCreateReactionEntry(messageId, emoji);
		Users.cacheUsers(users.slice());
		entry.users = {};
		for (const userPartial of users) {
			entry.users[userPartial.id] = new User(userPartial);
		}
		entry.lastUserId = users.length > 0 ? users[users.length - 1].id : null;
		entry.initialFetchLimit = Math.max(entry.initialFetchLimit, requestedLimit ?? users.length);
		entry.hasMore = inferHasMore({entry, pageUserCount: users.length, requestedLimit, responseHasMore, totalCount});
		entry.fetchStatus = 'success';
	}
	handleFetchAppend(
		messageId: string,
		users: ReadonlyArray<UserPartial>,
		emoji: ReactionEmoji,
		requestedLimit?: number,
		responseHasMore?: boolean,
		totalCount?: number,
	): void {
		const entry = this.getReactionEntry(messageId, emoji);
		if (!entry) {
			this.handleFetchSuccess(messageId, users, emoji, requestedLimit, responseHasMore, totalCount);
			return;
		}
		Users.cacheUsers(users.slice());
		for (const userPartial of users) {
			entry.users[userPartial.id] = new User(userPartial);
		}
		if (users.length > 0) {
			entry.lastUserId = users[users.length - 1].id;
		}
		entry.hasMore = inferHasMore({entry, pageUserCount: users.length, requestedLimit, responseHasMore, totalCount});
		entry.fetchStatus = 'success';
	}
	handleFetchError(messageId: string, emoji: ReactionEmoji): void {
		const entry = this.getOrCreateReactionEntry(messageId, emoji);
		entry.fetchStatus = 'error';
	}
}
export default new MessageReactions();
